/**
 * MenuEditor component
 * 7-day grid for editing weekly menu with lunch/dinner slots
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuEntryForm } from './MenuEntryForm';
import { AvailabilitySummary } from './AvailabilitySummary';
import {
  useWeekMenu,
  useAvailabilitySummary,
  useCreateMenu,
  useUpdateMenu,
  useDeleteMenu,
} from '@/hooks/useMenus';
import type { MealType, CreateMenuRequest } from '@/types/api';
import { formatDateForDisplay, formatDateForAPI } from '@/lib/utils/date';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface MenuEditorProps {
  /** Start date of the week (Monday) */
  weekStart: Date;
  /** User ID of the organizer */
  organizerId: number;
  /** Callback when save is successful */
  onSaveSuccess?: () => void;
}

interface MenuSlotState {
  date: string;
  mealType: MealType;
  menuId?: number;
}

/**
 * Menu editor with 7-day grid layout
 *
 * Features:
 * - 7-day week view (Monday-Sunday)
 * - Lunch and dinner slots per day
 * - Click to edit meal selection
 * - Remove menu entry button
 * - Availability summary per slot
 * - Real-time updates
 *
 * @example
 * ```tsx
 * function EditMenu() {
 *   const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
 *   const { user } = useAuth();
 *
 *   return (
 *     <MenuEditor
 *       weekStart={weekStart}
 *       organizerId={user.id}
 *       onSaveSuccess={() => console.log('Saved!')}
 *     />
 *   );
 * }
 * ```
 */
export function MenuEditor({
  weekStart,
  organizerId,
  onSaveSuccess,
}: MenuEditorProps) {
  const { t } = useTranslation();
  const [editingSlot, setEditingSlot] = useState<MenuSlotState | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const weekStartDate = formatDateForAPI(weekStart);

  // Fetch week menu
  const { data: menuData, isLoading: isMenuLoading } = useWeekMenu({
    startDate: weekStartDate,
  });

  // Fetch availability summary
  const { data: availabilityData, isLoading: isAvailabilityLoading } =
    useAvailabilitySummary({
      startDate: menuData?.week.startDate || weekStartDate,
      endDate:
        menuData?.week.endDate ||
        formatDateForAPI(
          new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
        ),
    });

  // Mutations
  const createMenuMutation = useCreateMenu();
  const updateMenuMutation = useUpdateMenu();
  const deleteMenuMutation = useDeleteMenu();

  const isLoading = isMenuLoading || isAvailabilityLoading;
  const isSaving =
    createMenuMutation.isPending ||
    updateMenuMutation.isPending ||
    deleteMenuMutation.isPending;

  // Get availability summary for a specific slot
  const getSlotAvailability = (date: string, mealType: MealType) => {
    return availabilityData?.summary.find(
      (item) => item.date === date && item.mealType === mealType
    );
  };

  const handleEditSlot = (date: string, mealType: MealType, menuId?: number) => {
    setEditingSlot({ date, mealType, menuId });
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: Omit<CreateMenuRequest, 'date' | 'mealType' | 'createdBy'>) => {
    if (!editingSlot) return;

    try {
      if (editingSlot.menuId) {
        await updateMenuMutation.mutateAsync({
          id: editingSlot.menuId,
          data: { tier: data.tier, recipeId: data.recipeId, label: data.label, energyKcal: data.energyKcal, proteins: data.proteins, carbohydrates: data.carbohydrates, fat: data.fat, fiber: data.fiber },
        });
      } else {
        await createMenuMutation.mutateAsync({
          ...data,
          date: editingSlot.date,
          mealType: editingSlot.mealType,
          createdBy: organizerId,
        });
      }

      setIsFormOpen(false);
      setEditingSlot(null);
      onSaveSuccess?.();
    } catch (error) {
      console.error('Error saving menu:', error);
    }
  };

  // Handle remove menu entry
  const handleRemoveMenu = async (menuId: number) => {
    if (confirm(t('menu.confirmRemove'))) {
      try {
        await deleteMenuMutation.mutateAsync(menuId);
        onSaveSuccess?.();
      } catch (error) {
        console.error('Error removing menu:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-4 h-64 animate-pulse"
          >
            <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4" />
            <div className="space-y-3">
              <div className="h-20 bg-gray-200 rounded" />
              <div className="h-20 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('menu.unableToLoad')}</p>
      </div>
    );
  }

  const TIER_STYLES = {
    recipe: 'bg-green-100 text-green-800',
    semi: 'bg-yellow-100 text-yellow-800',
    cheat: 'bg-red-100 text-red-800',
  } as const;

  const renderSlot = (date: string, mealType: MealType, label: string) => {
    const menu = menuData!.menus.find((d) => d.date === date)?.[mealType];

    return (
      <div className={`p-3 ${mealType === 'lunch' ? 'border-b border-gray-200' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-700 uppercase">{label}</h4>
          <AvailabilitySummary
            summary={getSlotAvailability(date, mealType)}
            isLoading={isAvailabilityLoading}
            compact
          />
        </div>

        {menu ? (
          <div className="space-y-2">
            <div className="p-2 bg-gray-50 rounded border border-gray-200">
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${TIER_STYLES[menu.tier]}`}>
                  {menu.tier}
                </span>
                <p className="text-sm font-medium text-gray-900">{menu.label}</p>
              </div>
              {menu.nutrition && (
                <p className="text-[11px] text-gray-500">
                  {Math.round(menu.nutrition.energyKcal)} kcal
                  {' · '}P {Math.round(menu.nutrition.proteins)}g
                  {' · '}G {Math.round(menu.nutrition.fat)}g
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleEditSlot(date, mealType, menu.id)}
                disabled={isSaving}
                className="flex-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50 transition-colors"
              >
                {t('menu.change')}
              </button>
              <button
                type="button"
                onClick={() => handleRemoveMenu(menu.id)}
                disabled={isSaving}
                className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 transition-colors"
              >
                {t('menu.remove')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => handleEditSlot(date, mealType)}
            disabled={isSaving}
            className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors disabled:opacity-50"
          >
            {t('menu.addMeal')}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Weekly nutrition summary */}
      {menuData.weekNutrition && menuData.avgPerMeal && (
        <div className="mb-6 bg-white rounded-lg shadow-md p-4 border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('menu.weeklyNutrition')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <NutritionStat label="kcal" total={menuData.weekNutrition.energyKcal} avg={menuData.avgPerMeal.energyKcal} />
            <NutritionStat label={t('menu.proteins')} total={menuData.weekNutrition.proteins} avg={menuData.avgPerMeal.proteins} unit="g" />
            <NutritionStat label={t('menu.carbs')} total={menuData.weekNutrition.carbohydrates} avg={menuData.avgPerMeal.carbohydrates} unit="g" />
            <NutritionStat label={t('menu.fat')} total={menuData.weekNutrition.fat} avg={menuData.avgPerMeal.fat} unit="g" />
            <NutritionStat label={t('menu.fiber')} total={menuData.weekNutrition.fiber} avg={menuData.avgPerMeal.fiber} unit="g" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        {menuData.menus.map((day) => {
          const dayDate = parseISO(day.date);
          const dayName = format(dayDate, 'EEEE', { locale: fr });
          const dayDisplay = formatDateForDisplay(dayDate);

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          const isPast = day.date < todayString;

          return (
            <div
              key={day.date}
              className={`bg-white rounded-lg shadow-md overflow-hidden transition-opacity ${isPast ? 'opacity-50' : ''}`}
            >
              <div className="bg-primary-700 text-white p-3 text-center">
                <div className="font-bold text-sm">{dayName}</div>
                <div className="text-xs opacity-90">{dayDisplay}</div>
              </div>

              {renderSlot(day.date, 'lunch', t('availability.lunch'))}
              {renderSlot(day.date, 'dinner', t('availability.dinner'))}
            </div>
          );
        })}
      </div>

      {/* Menu entry form modal */}
      {isFormOpen && editingSlot && (
        <MenuEntryForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingSlot(null);
          }}
          onSubmit={handleFormSubmit}
          title={t('menu.selectMealFor', {
            mealType: editingSlot.mealType === 'lunch' ? t('availability.lunch') : t('availability.dinner')
          })}
        />
      )}
    </>
  );
}

function NutritionStat({ label, total, avg, unit = '' }: { label: string; total: number; avg: number; unit?: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{Math.round(avg)}{unit}</div>
      <div className="text-[10px] text-gray-400">moy/repas ({Math.round(total)}{unit} total)</div>
    </div>
  );
}
